<script lang="ts">
    import { onMount } from "svelte";
    import { apiClient } from "./apiClient";
    import { tags, loading, error, success } from "./stores";
    import type { Tag } from "./apiClient";

    let showForm = false;
    let newTagName = "";
    let editingTagId: number | null = null;

    onMount(async () => {
        await loadTags();
    });

    async function loadTags() {
        try {
            loading.set(true);
            error.set(null);

            const tagsData = await apiClient.getTags();
            tags.set(tagsData);
        } catch (err) {
            error.set(
                err instanceof Error ? err.message : "Failed to load tags",
            );
        } finally {
            loading.set(false);
        }
    }

    async function addTag() {
        if (!newTagName.trim()) {
            error.set("Tag name is required");
            return;
        }

        try {
            loading.set(true);
            error.set(null);

            await apiClient.createTag({ name: newTagName.trim() });
            newTagName = "";
            showForm = false;
            await loadTags();
            success.set("Tag added successfully");
        } catch (err) {
            error.set(err instanceof Error ? err.message : "Failed to add tag");
        } finally {
            loading.set(false);
        }
    }

    async function deleteTag(tag: Tag) {
        if (!confirm(`Are you sure you want to delete "${tag.name}"?`)) {
            return;
        }

        try {
            loading.set(true);
            error.set(null);

            await apiClient.deleteTag(tag.id);
            await loadTags();
            success.set("Tag deleted successfully");
        } catch (err) {
            error.set(
                err instanceof Error ? err.message : "Failed to delete tag",
            );
        } finally {
            loading.set(false);
        }
    }

    function toggleForm() {
        showForm = !showForm;
        if (!showForm) {
            newTagName = "";
            editingTagId = null;
        } else {
            editingTagId = -1; // Special ID for new tag
        }
    }
</script>

<div class="section">
    <h2>Tags Management</h2>

    <button class="btn btn-primary" on:click={toggleForm}>
        {showForm ? "Cancel" : "Add New Tag"}
    </button>

    {#if showForm && editingTagId === -1}
        <div class="inline-form">
            <div class="form-group">
                <label for="tag-name">Tag Name</label>
                <input
                    id="tag-name"
                    type="text"
                    bind:value={newTagName}
                    placeholder="Enter tag name"
                    on:keydown={(e) => e.key === "Enter" && addTag()}
                />
                <button
                    class="btn btn-success"
                    on:click={addTag}
                    disabled={$loading}
                >
                    {$loading ? "Adding..." : "Add Tag"}
                </button>
            </div>
        </div>
    {/if}

    {#if $loading}
        <div class="loading">Loading...</div>
    {/if}

    {#if $tags.length > 0}
        <table class="table">
            <thead>
                <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {#each $tags as tag}
                    <tr>
                        <td data-label="ID">{tag.id}</td>
                        <td data-label="Name">{tag.name}</td>
                        <td data-label="Actions" class="actions">
                            <button
                                class="btn btn-danger"
                                on:click={() => deleteTag(tag)}
                                disabled={$loading}
                            >
                                Delete
                            </button>
                        </td>
                    </tr>
                {/each}
            </tbody>
        </table>
    {:else if !$loading}
        <p>No tags found. Add your first tag!</p>
    {/if}
</div>

<style>
    .inline-form {
        padding: 20px;
        border: 2px solid #e9ecef;
        border-radius: 8px;
        background-color: #fff;
        margin: 10px 0;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
</style>
